import React, { useCallback } from "react";
import {
  Box,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { LabeledField } from "@/__core/components/LabeledField";
import { UserOptionDisplay } from "@/__core/components";
import { sharedFormStyles } from "@/__core/styles/sharedFormStyles";
import CoreRoleSelector from "@/__core/components/AutoSelector";
import { AsyncSearchAutocomplete } from "@/__core/hocs";
import { UserModel } from "@/modules/users/models/UserModel";
import { UserSelectionType } from "@/modules/employees/models/Employee";

interface AccountInfoProps {
  userSelectionType: UserSelectionType;
  isEditMode: boolean;
  canEdit: boolean;
  errors: { [key: string]: string };
  selectedUser: UserModel | null;
  availableUsersEnabled: boolean;
  fetchAvailableUsers: (search: string) => Promise<UserModel[]>;
  roles: string[];
  password: string;
  confirmPassword: string;
  email: string;
  roleSelectorCompanyIds?: string[];
  roleSelectorGroupByCompany?: boolean;
  getUserDisplayName: (user: Partial<UserModel> & Record<string, any>) => string;
  onUserSelectionTypeChange: (type: UserSelectionType) => void;
  onUserSelect: (user: UserModel | null) => void;
  onFieldChange: (field: string, value: any) => void;
}

const AccountInfo: React.FC<AccountInfoProps> = ({
  userSelectionType,
  isEditMode,
  canEdit,
  errors,
  selectedUser,
  availableUsersEnabled,
  fetchAvailableUsers,
  roles,
  password,
  confirmPassword,
  email,
  roleSelectorCompanyIds,
  roleSelectorGroupByCompany,
  getUserDisplayName,
  onUserSelectionTypeChange,
  onUserSelect,
  onFieldChange,
}) => {
  const getUserOptionLabel = useCallback(
    (option: UserModel) => {
      const displayName = getUserDisplayName(option as any);
      const email = String((option as any)?.email || (option as any)?.user_email || "").trim();

      if (displayName && email) {
        return `${displayName} (${email})`;
      }

      return displayName || email || "Unknown User";
    },
    [getUserDisplayName]
  );

  return (
    <Paper sx={{ ...sharedFormStyles.formCard, mb: 0 }}>
      <Typography sx={sharedFormStyles.sectionTitle}>Thông tin tài khoản</Typography>

      <Box>
        <RadioGroup
          row
          value={userSelectionType}
          sx={sharedFormStyles.radioGroupRow}
          onChange={(e) => onUserSelectionTypeChange(e.target.value as UserSelectionType)}
        >
          <FormControlLabel
            value="new"
            sx={sharedFormStyles.radioGroupLabel}
            control={
              <Radio
                size="small"
                disabled={!canEdit}
                sx={{
                  "&.Mui-checked": { color: "#3b82f6" },
                }}
              />
            }
            label="Tạo User mới"
          />
          <FormControlLabel
            value="existing"
            sx={sharedFormStyles.radioGroupLabel}
            control={
              <Radio
                size="small"
                disabled={!canEdit}
                sx={{
                  "&.Mui-checked": { color: "#3b82f6" },
                }}
              />
            }
            label="Chọn User đã tồn tại"
          />
          <FormControlLabel
            value="no-user"
            sx={sharedFormStyles.radioGroupLabel}
            control={
              <Radio
                size="small"
                disabled={!canEdit}
                sx={{
                  "&.Mui-checked": { color: "#3b82f6" },
                }}
              />
            }
            label="Không tạo user"
          />
        </RadioGroup>

        {userSelectionType === "existing" && (
          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <LabeledField
              label={isEditMode ? "User" : "Chọn User"}
              required
              labelWidth={80}
            >
              <AsyncSearchAutocomplete<UserModel>
                key="employee-existing-user-picker"
                fullWidth
                enabled={availableUsersEnabled}
                fetchOptions={fetchAvailableUsers}
                value={selectedUser}
                onChange={(_event, newValue) => onUserSelect(newValue)}
                getOptionLabel={getUserOptionLabel}
                getOptionId={(option) => option.id}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!canEdit}
                noOptionsTextWhenEmpty="Nhập để tìm user chưa gán nhân viên"
                noOptionsTextWhenSearching="Không tìm thấy user — thử từ khóa khác"
                textFieldProps={{
                  error: !!errors.user,
                  disabled: !canEdit,
                  placeholder: isEditMode
                    ? "User đã được gán"
                    : "Tìm kiếm user theo họ tên, username hoặc email...",
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <UserOptionDisplay user={option as any} />
                  </Box>
                )}
              />
            </LabeledField>
          </Grid>
        )}

        {userSelectionType !== "no-user" && (
          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <CoreRoleSelector
              value={roles || []}
              onChange={(nextRoles) => onFieldChange("roles", nextRoles)}
              disabled={!canEdit}
              label="Vai trò"
              companyIds={roleSelectorCompanyIds}
              groupByCompany={roleSelectorGroupByCompany}
            />
          </Grid>
        )}

        {(userSelectionType === "new" || (isEditMode && userSelectionType !== "no-user")) && (
          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <Grid container rowSpacing={1} columnSpacing={5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledField label="Email" required={userSelectionType === "new"} labelWidth={80}>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    value={email}
                    onChange={(e) => onFieldChange("email", e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={!canEdit || isEditMode}
                    autoComplete="off"
                  />
                </LabeledField>
              </Grid>
            </Grid>
          </Grid>
        )}

        {userSelectionType === "new" && (
          <Grid size={{ xs: 12 }}>
            <Grid container rowSpacing={1} columnSpacing={5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledField label="Mật khẩu" labelWidth={80} alignItems="end">
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    value={password}
                    onChange={(e) => onFieldChange("password", e.target.value)}
                    error={!!errors.password}
                    helperText={errors.password}
                    disabled={!canEdit}
                    autoComplete="new-password"
                  />
                </LabeledField>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledField label="Xác nhận mật khẩu" alignItems="end">
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => onFieldChange("confirmPassword", e.target.value)}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    disabled={!canEdit}
                    autoComplete="new-password"
                  />
                </LabeledField>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
    </Paper>
  );
};

export default AccountInfo;
